import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { SmartRoomProfileConfig, ResidentialPropertyType, SmartProfileThreshold } from '../types/admin';

const ALL_PROPERTY_TYPES: ResidentialPropertyType[] = [
  'single_family', 'condo', 'townhouse', 'apartment', 'manufactured', 'multi_family',
];

const DEFAULT_THRESHOLDS: SmartProfileThreshold[] = [
  { level: 'minimal', maxSqft: undefined },
  { level: 'standard', maxSqft: undefined },
  { level: 'heavy', maxSqft: undefined },
  { level: 'full' },
];

function ensureAllPropertyTypes(configs: SmartRoomProfileConfig[]): SmartRoomProfileConfig[] {
  return ALL_PROPERTY_TYPES.map(pt => {
    const existing = configs.find(c => c.propertyType === pt);
    return existing ?? { propertyType: pt, thresholds: [...DEFAULT_THRESHOLDS] };
  });
}

interface SmartProfilesResponse {
  success: boolean;
  data: SmartRoomProfileConfig[];
}

export function useSmartRoomProfiles() {
  const [configs, setConfigs] = useState<SmartRoomProfileConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get<SmartProfilesResponse>('/admin/smart-room-profiles')
      .then(res => {
        if (!cancelled) {
          setConfigs(ensureAllPropertyTypes(res.data ?? []));
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message ?? 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const saveConfigs = useCallback(async (updated: SmartRoomProfileConfig[]) => {
    const res = await api.put<SmartProfilesResponse>('/admin/smart-room-profiles', { configs: updated });
    setConfigs(ensureAllPropertyTypes(res.data ?? []));
  }, []);

  return { configs, loading, error, saveConfigs };
}
